// Admin setup-readiness checks. They mirror how the backend resolves configuration
// (backend/src/services/cvi-calculation.service.js) and never calculate scores.
//
// Position weights used by the backend, in order of precedence:
//   collective:  position.collectiveContributionProfile (team/upstream/organization)
//                → position team/department/organization weights (no default)
//   individual:  position.individualPerformanceProfile (tasks/goals/peer feedback)
//                → position tasksActivities/individualGoal/peerFeedback weights → 50/30/20
//   task split:  profile routine/nonRoutine → position routine/nonRoutine weights → 60/40
// Weights are normalized by the backend, so a position is usable when each group has a
// positive total; exact totals of 100 are not required.

const num = value => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
};
const firstDefined = (...values) => values.find(value => value !== undefined && value !== null);

export function effectivePositionWeights(position) {
    const collective = position?.collectiveProfile;
    const individual = position?.individualProfile;
    return {
        collective: {
            team: num(firstDefined(collective?.teamWeight, position?.wTeam, 0)),
            upstream: num(firstDefined(collective?.upstreamWeight, position?.wDept, 0)),
            organization: num(firstDefined(collective?.organizationWeight, position?.wOrg, 0)),
        },
        individual: {
            tasksActivities: num(firstDefined(individual?.tasksWeight, position?.wTasksActivities, 50)),
            goals: num(firstDefined(individual?.goalsWeight, position?.wGoals, 30)),
            peerFeedback: num(firstDefined(individual?.peerFeedbackWeight, position?.wPeerFeedback, 20)),
        },
        split: {
            routine: num(firstDefined(individual?.routineWeight, position?.wRoutine, 60)),
            nonRoutine: num(firstDefined(individual?.nonRoutineWeight, position?.wNonRoutine, 40)),
        },
        source: { collective: collective ? "template" : "position", individual: individual ? "template" : "position" },
    };
}

const positiveTotal = group => Object.values(group).some(value => value < 0) ? false : Object.values(group).reduce((sum, value) => sum + value, 0) > 0;

export function positionReadiness(positions = []) {
    const notReady = positions.filter(position => {
        const weights = effectivePositionWeights(position);
        return !(positiveTotal(weights.collective) && positiveTotal(weights.individual) && positiveTotal(weights.split));
    });
    return { ready: positions.length - notReady.length, total: positions.length, ok: positions.length > 0 && notReady.length === 0, notReady };
}

const EVALUATED_ROLES = ["specialist", "manager", "deputy"];

// A unit is ready when the backend can score core responsibilities for its people: at least one
// active question with a positive weight scoped to the unit, and a rating scale with a positive
// score. Only units with evaluated people are counted; empty units do not need questions.
export function unitRoutineReadiness({ orgUnits = [], users = [], routineQuestions = [], routineLevels = [] }) {
    const scaleReady = routineLevels.some(level => num(level.score) > 0);
    const units = orgUnits.filter(unit => unit.isActive !== false
        && users.some(user => user.isActive !== false && user.orgUnitId === unit.id && EVALUATED_ROLES.includes(user.role)));
    const ready = units.filter(unit => routineQuestions.some(question =>
        question.scope === "org_unit" && question.orgUnitId === unit.id && question.isActive !== false && num(question.weight) > 0));
    return { ready: scaleReady ? ready.length : 0, total: units.length, scaleReady, ok: scaleReady && units.length > 0 && ready.length === units.length };
}
